import { useState } from "react";
import { C, S, fontCond, RULES_SECTIONS } from "../constants";
import { Avatar, StatCard, WinRateBar } from "../components/ui";
import { getPlayerStats } from "../lib/helpers";

// ── Pagination helper ─────────────────────────────────────────────────────────
function Pagination({ page, total, perPage, onChange }) {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;
  const btnStyle = active => ({
    minWidth: 32, height: 32, borderRadius: 6,
    border: "1px solid " + (active ? C.primary : C.border),
    background: active ? C.primary : "transparent",
    color: active ? "#fff" : C.text,
    fontFamily: fontCond, fontWeight: 700, fontSize: 13,
    cursor: active ? "default" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px",
  });
  const navStyle = disabled => ({
    ...btnStyle(false),
    color: disabled ? C.border : C.muted,
    cursor: disabled ? "default" : "pointer",
  });
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
      <button style={navStyle(page === 1)} disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
      {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
        <button key={p} style={btnStyle(p === page)} onClick={() => p !== page && onChange(p)}>{p}</button>
      ))}
      <button style={navStyle(page === pages)} disabled={page === pages} onClick={() => onChange(page + 1)}>›</button>
    </div>
  );
}

// ── RankBadge helper ──────────────────────────────────────────────────────────
function RankBadge({ position }) {
  if (!position) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontFamily: fontCond, fontWeight: 800, fontSize: 10,
      color: C.muted, background: C.border + "88",
      borderRadius: 4, padding: "1px 5px", marginLeft: 5,
      lineHeight: 1, flexShrink: 0, verticalAlign: "middle",
    }}>
      #{position}
    </span>
  );
}

// ── ResultadosScreen (public) ─────────────────────────────────────────────────
const RESULTADOS_PER_PAGE = 15;

export function ResultadosScreen({ state, isMobile }) {
  const [page, setPage] = useState(1);
  const [filterPlayer, setFilterPlayer] = useState("");

  const playerNames = [...new Set(
    state.results.flatMap(r => [r.challengerName, r.challengedName])
  )].sort();

  const allResults = [...state.results].reverse();
  const filtered = filterPlayer
    ? allResults.filter(r =>
        r.challengerName?.toLowerCase().includes(filterPlayer.toLowerCase()) ||
        r.challengedName?.toLowerCase().includes(filterPlayer.toLowerCase())
      )
    : allResults;
  const slice = filtered.slice((page - 1) * RESULTADOS_PER_PAGE, page * RESULTADOS_PER_PAGE);

  const handleFilter = (name) => { setFilterPlayer(name); setPage(1); };

  return (
    <div>
      {/* Filtro — campo de texto */}
      <div style={{ position: "relative", marginBottom: 12 }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 15, color: C.muted, pointerEvents: "none" }}>🔍</span>
        <input
          type="text"
          placeholder="Buscar jogador..."
          value={filterPlayer}
          onChange={e => handleFilter(e.target.value)}
          style={{ ...S.input, marginBottom: 0, paddingLeft: 36, width: "100%", boxSizing: "border-box", fontSize: 13 }}
        />
        {filterPlayer && (
          <button
            onClick={() => handleFilter("")}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", color: C.muted, fontSize: 16, lineHeight: 1, padding: 4 }}
          >✕</button>
        )}
      </div>

      <div style={isMobile ? S.cardMobile : S.card}>
        <div style={{ ...S.title, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>📋 Histórico de Resultados</span>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{filtered.length} resultado{filtered.length !== 1 ? "s" : ""}</span>
        </div>
        {filtered.length === 0 && <div style={{ color: C.muted }}>Nenhum resultado encontrado.</div>}
        {isMobile ? (
          <div>
            {slice.map((r, i) => {
              const chrWon = r.type === "wo-challenged" || (r.type !== "wo-challenger" && r.winner === "challenger");
              const chdWon = r.type === "wo-challenger"  || (r.type !== "wo-challenged" && r.winner === "challenged");
              const chrPlayer = state.players?.find(p => p.name === r.challengerName);
              const chdPlayer = state.players?.find(p => p.name === r.challengedName);
              return (
                <div key={i} style={{ padding: "10px 0", borderBottom: "1px solid " + C.border }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: C.muted }}>{new Date(r.date).toLocaleDateString("pt-BR")}</span>
                    {r.type !== "normal" ? <span style={S.badge(C.warning)}>W.O.</span> : null}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ fontWeight: chrWon ? 700 : 400, color: chrWon ? C.success : C.muted }}>
                      {r.challengerName}<RankBadge position={chrPlayer?.position}/>
                    </span>
                    <span style={{ margin: "0 6px", color: C.muted }}>vs</span>
                    <span style={{ fontWeight: chdWon ? 700 : 400, color: chdWon ? C.info : C.muted }}>
                      {r.challengedName}<RankBadge position={chdPlayer?.position}/>
                    </span>
                    {r.score && <span style={{ fontFamily: fontCond, fontWeight: 700, marginLeft: 8, color: C.text }}>{r.score}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Data</th>
                <th style={S.th}>Desafiante</th>
                <th style={S.th}>Desafiado</th>
                <th style={S.th}>Placar</th>
                <th style={S.th}>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((r, i) => {
                const chrWon = r.type === "wo-challenged" || (r.type !== "wo-challenger" && r.winner === "challenger");
                const chdWon = r.type === "wo-challenger"  || (r.type !== "wo-challenged" && r.winner === "challenged");
                const chrPlayer = state.players?.find(p => p.name === r.challengerName);
                const chdPlayer = state.players?.find(p => p.name === r.challengedName);
                return (
                  <tr key={i}>
                    <td style={{ ...S.td, fontSize: 11 }}>{new Date(r.date).toLocaleDateString("pt-BR")}</td>
                    <td style={{ ...S.td, fontWeight: chrWon ? 700 : 400, color: chrWon ? C.success : C.muted }}>
                      {r.challengerName}<RankBadge position={chrPlayer?.position}/>
                    </td>
                    <td style={{ ...S.td, fontWeight: chdWon ? 700 : 400, color: chdWon ? C.info : C.muted }}>
                      {r.challengedName}<RankBadge position={chdPlayer?.position}/>
                    </td>
                    <td style={{ ...S.td, fontFamily: fontCond, fontWeight: 700 }}>{r.score || "—"}</td>
                    <td style={S.td}>
                      {r.type === "wo-challenged" || r.type === "wo-challenger"
                        ? <span style={S.badge(C.warning)}>W.O.</span>
                        : r.winner === "challenger"
                        ? <span style={S.badge(C.success)}>Desafiante</span>
                        : <span style={S.badge(C.info)}>Desafiado</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination page={page} total={filtered.length} perPage={RESULTADOS_PER_PAGE} onChange={setPage}/>
      </div>
    </div>
  );
}

// ── StatsScreen ───────────────────────────────────────────────────────────────
export function StatsScreen({ state, sorted, setStatsModal, isMobile }) {
  const topWins = sorted
    .map(p => ({ ...p, ...getPlayerStats(p.name, state.results) }))
    .filter(p => p.wins > 0)
    .sort((a, b) =>
      b.wins - a.wins ||
      a.losses - b.losses ||
      b.winRate - a.winRate ||
      a.position - b.position
    )
    .slice(0, 10);

  const totalResults = state.results.length;
  const challengerWins = state.results.filter(r =>
    r.winner === "challenger" || r.type === "wo-challenged"
  ).length;
  const challengedWins = totalResults - challengerWins;
  const chrPct = totalResults > 0 ? Math.round((challengerWins / totalResults) * 100) : 50;
  const chdPct = 100 - chrPct;

  return (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10, alignItems: "start" }}>

      {/* COLUNA ESQUERDA: 4 cards 2x2 + barra */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

        {/* 4 stat cards — 2x2 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <StatCard label="Jogadores" value={sorted.length}                                                     color={C.info}/>
          <StatCard label="Partidas"  value={totalResults}                                                      color={C.accent}/>
          <StatCard label="Desafios"  value={state.challenges.length}                                           color={C.warning}/>
          <StatCard label="W.O.s"     value={state.results.filter(r => r.type && r.type !== "normal").length}   color={C.primary}/>
        </div>

        {/* Desafiante vs Desafiado */}
        <div style={isMobile ? S.cardMobile : S.card}>
          <div style={S.title}>⚔️ Desafiante vs Desafiado</div>
          {totalResults === 0 ? (
            <div style={{ color: C.muted, fontSize: 13 }}>Nenhum resultado ainda.</div>
          ) : (
            <>
              <div style={{ display: "flex", height: 20, borderRadius: 6, overflow: "hidden", margin: "14px 0 12px" }}>
                <div style={{ width: chrPct + "%", background: C.accent, transition: "width .4s" }}/>
                <div style={{ width: chdPct + "%", background: C.primary, transition: "width .4s" }}/>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: C.accent, display: "inline-block", flexShrink: 0 }}/>
                  <span style={{ color: C.muted }}>Desafiante</span>
                  <span style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 16, color: C.accent }}>{chrPct}%</span>
                  <span style={{ fontSize: 11, color: C.muted }}>({challengerWins}V)</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 11, color: C.muted }}>({challengedWins}V)</span>
                  <span style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 16, color: C.primary }}>{chdPct}%</span>
                  <span style={{ color: C.muted }}>Desafiado</span>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: C.primary, display: "inline-block", flexShrink: 0 }}/>
                </div>
              </div>
              <div style={{ textAlign: "center", marginTop: 12, fontSize: 11, color: C.muted }}>
                {totalResults} partida{totalResults !== 1 ? "s" : ""} disputada{totalResults !== 1 ? "s" : ""}
              </div>
            </>
          )}
        </div>

      </div>

      {/* COLUNA DIREITA: ranking de vitórias */}
      <div style={isMobile ? S.cardMobile : S.card}>
        <div style={S.title}>🏆 Mais Vitórias</div>
        {topWins.length === 0 && <div style={{ color: C.muted, fontSize: 13 }}>Nenhum resultado ainda.</div>}
        {topWins.map((p, i) => (
          <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid " + C.border + "55", cursor: "pointer" }} onClick={() => setStatsModal(p)}>
            <div style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 14, color: i < 3 ? C.warning : C.muted, width: 22, textAlign: "center", flexShrink: 0 }}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":i+1}</div>
            <Avatar player={p} size={28}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.name}<RankBadge position={p.position}/>
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <span style={{ fontFamily: fontCond, fontWeight: 900, fontSize: 15, color: C.success }}>{p.wins}V</span>
              <span style={{ fontSize: 11, color: "#e53e3e", marginLeft: 4 }}>{p.losses}D</span>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

// ── RegrasScreen ──────────────────────────────────────────────────────────────
export function RegrasScreen({ state, isMobile }) {
  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid " + C.border }}>
        <div style={{ fontFamily: fontCond, fontSize: 24, fontWeight: 900, color: C.text, marginBottom: 4 }}>Regulamento Oficial</div>
        <div style={{ color: C.muted, fontSize: 13 }}>Clube dos Caiçaras — Ranking de Tênis 2026</div>
        <a
          href="https://mcyiufzzwcznaspfdfoy.supabase.co/storage/v1/object/public/Regulamento%20Ranking/regulamento_cdc_ranking_2026_v2.pdf"
          target="_blank" rel="noopener noreferrer" download
          style={{ ...S.btn(C.primary), marginTop: 16, display: "inline-flex", gap: 6, textDecoration: "none", padding: "8px 16px", fontSize: 13, fontWeight: 600, minHeight: 36 }}
        >
          📄 Baixar Regulamento Completo
        </a>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(360px, 1fr))", gap: 12 }}>
        {(state.rules || RULES_SECTIONS).map((sec, i) => (
          <div key={i} style={isMobile ? S.cardMobile : S.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ fontFamily: fontCond, fontWeight: 800, fontSize: 16, color: C.text }}>{sec.title}</div>
            </div>
            <div style={{ fontSize: 15.5, color: "#3d4f63", lineHeight: 1.9, whiteSpace: "pre-line" }}>{sec.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
